import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Select from 'react-select';
import { useGetDoctorQuery, useGetTopicsQuery, useCreateDoctorMutation, useUpdateDoctorMutation } from '../../../store/api/adminApi';
import PageLoader from '../../../components/PageLoader';
import ImageUpload from '../../../components/ImageUpload';
import { showSuccess, showError } from '../../../utils/toast';

const AdminDoctorForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  
  const { data, isLoading } = useGetDoctorQuery(id!, { skip: !isEdit });
  const { data: topicsData } = useGetTopicsQuery({ per_page: 1000 }); // Get all topics with large per_page
  const [createDoctor, { isLoading: isCreating }] = useCreateDoctorMutation();
  const [updateDoctor, { isLoading: isUpdating }] = useUpdateDoctorMutation();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    topic_ids: [] as number[],
    picture: null as File | null,
  });

  const [existingImage, setExistingImage] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (isEdit && data?.data) {
      const doctor = data.data;
      setFormData({
        name: doctor.name || '',
        email: doctor.email || '',
        phone: doctor.phone || '',
        password: '',
        topic_ids: doctor.topics?.map((t: any) => t.id) || [],
        picture: null,
      });
      // Extract filename from full URL if needed
      if (doctor.picture) {
        const imageFilename = doctor.picture.includes('/uploads/') 
          ? doctor.picture.split('/uploads/')[1] 
          : doctor.picture;
        setExistingImage(imageFilename);
      } else {
        setExistingImage(undefined);
      }
    }
  }, [isEdit, data]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleTopicChange = (selectedOptions: any) => {
    const topicIds = selectedOptions ? selectedOptions.map((option: any) => option.value) : [];
    setFormData(prev => ({ ...prev, topic_ids: topicIds }));
  };

  const handleImageChange = (file: File | null) => {
    setFormData(prev => ({ ...prev, picture: file }));
  };

  const handleRemoveExistingImage = () => {
    setExistingImage(undefined);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const submitData = new FormData();
      submitData.append('name', formData.name);
      submitData.append('email', formData.email);
      submitData.append('phone', formData.phone);
      submitData.append('topic_ids', JSON.stringify(formData.topic_ids));

      if (formData.password) {
        submitData.append('password', formData.password);
      }

      // Append picture if a new one is selected
      if (formData.picture) {
        submitData.append('picture', formData.picture);
      }

      if (isEdit) {
        const result = await updateDoctor({ id: parseInt(id!), data: submitData }).unwrap();
        showSuccess(result.message || 'Doctor updated successfully!');
      } else {
        if (!formData.password) {
          showError('Password is required for new doctors');
          return;
        }
        const result = await createDoctor(submitData).unwrap();
        showSuccess(result.message || 'Doctor created successfully!');
      }
      
      navigate('/admin/doctors');
    } catch (err: any) {
      showError(err?.data?.message || `Failed to ${isEdit ? 'update' : 'create'} doctor. Please try again.`);
    }
  };

  if (isLoading) {
    return <PageLoader />;
  }

  // Extract topics from paginated response
  const topics = topicsData?.data?.data || topicsData?.data || [];
  
  // Format topics for react-select
  const topicOptions = topics.map((topic: any) => ({
    value: topic.id,
    label: topic.name || topic.title || topic.name_ar || `Topic ${topic.id}`,
  }));
  
  // Get selected topics for react-select
  const selectedTopics = topicOptions.filter((option: any) => 
    formData.topic_ids.includes(option.value)
  );

  return (
    <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
      <div className="d-flex flex-column flex-column-fluid">
        <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
          <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
            <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
              <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
                {isEdit ? 'Edit Doctor' : 'Add New Doctor'}
              </h1>
              <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                <li className="breadcrumb-item text-muted">
                  <a href="#" onClick={(e) => { e.preventDefault(); navigate('/admin/dashboard'); }} className="text-muted text-hover-primary">Home</a>
                </li>
                <li className="breadcrumb-item">
                  <span className="bullet bg-gray-400 w-5px h-2px"></span>
                </li>
                <li className="breadcrumb-item text-muted">
                  <a href="#" onClick={(e) => { e.preventDefault(); navigate('/admin/doctors'); }} className="text-muted text-hover-primary">Doctors</a>
                </li>
              </ul>
            </div>
          </div>
        </div>
        
        <div id="kt_app_content" className="app-content flex-column-fluid">
          <div id="kt_app_content_container" className="app-container container-xxl">
            <div className="card">
              <div className="card-body pt-0">
                <form onSubmit={handleSubmit} id="kt_modal_new_target_form" className="form fv-plugins-bootstrap5 fv-plugins-framework">
                  <div className="row g-9 mb-8">
                    <div className="col-md-6">
                      <div className="d-flex flex-column fv-row fv-plugins-icon-container">
                        <label className="d-flex align-items-center fs-6 fw-semibold mb-2">
                          <span className="required"> Name</span>
                        </label>
                        <input
                          type="text"
                          className="form-control form-control-solid"
                          placeholder="Enter Doctor name"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                    </div>
                    
                    <div className="col-md-6 fv-row fv-plugins-icon-container">
                      <label className="required fs-6 fw-semibold mb-2">Email Address</label>
                      <input
                        type="email"
                        required
                        className="form-control form-control-solid"
                        placeholder="Enter Doctor Email Address"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                      />
                    </div>
                    
                    <div className="col-md-6 fv-row fv-plugins-icon-container">
                      <label className={isEdit ? "fs-6 fw-semibold mb-2" : "required fs-6 fw-semibold mb-2"}>
                      Password {isEdit ?  <small>(Password can't be viewed but can update from here)</small> : ""}
                      </label>
                      <input
                        type="text"
                        className="form-control form-control-solid"
                        placeholder="Enter Password"
                        name="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        required={!isEdit}
                      />
                    </div>
                    
                    <div className="col-md-6 fv-row fv-plugins-icon-container">
                      <label className="fs-6 fw-semibold mb-2">Phone No</label>
                      <input
                        type="number"
                        className="form-control form-control-solid"
                        placeholder="Enter Phone Number"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                      />
                    </div>
                    
                    <div className="col-md-6 fv-row fv-plugins-icon-container">
                      <label className="required fs-6 fw-semibold mb-2">Topic:</label>
                      <Select
                        isMulti
                        isSearchable
                        options={topicOptions}
                        value={selectedTopics}
                        onChange={handleTopicChange}
                        placeholder="Select Doctor Categories"
                        className="react-select-container"
                        classNamePrefix="react-select"
                        styles={{
                          control: (base) => ({
                            ...base,
                            minHeight: '48px',
                            borderColor: '#e4e6ef',
                            '&:hover': {
                              borderColor: '#e4e6ef',
                            },
                          }),
                          multiValue: (base) => ({
                            ...base,
                            backgroundColor: '#f1faff',
                          }),
                          multiValueLabel: (base) => ({
                            ...base,
                            color: '#009ef7',
                          }),
                          multiValueRemove: (base) => ({
                            ...base,
                            color: '#009ef7',
                            '&:hover': {
                              backgroundColor: '#009ef7',
                              color: 'white',
                            },
                          }),
                        }}
                      />
                    </div>
                    
                    <div className="col-md-6 fv-row fv-plugins-icon-container">
                      <ImageUpload
                        existingImage={existingImage}
                        onImageChange={handleImageChange}
                        onRemoveExisting={handleRemoveExistingImage}
                        accept=".png, .jpg, .jpeg"
                        label="Upload Picture"
                        name="picture"
                      />
                    </div>
                  </div>
                  
                  <div className="text-center">
                    <button type="button" id="kt_modal_new_target_cancel" className="btn btn-light me-3" onClick={() => navigate('/admin/doctors')}>
                      Discard
                    </button>
                    <button type="submit" id="kt_modal_new_target_submit" className="btn btn-base" disabled={isCreating || isUpdating}>
                      <span className="indicator-label">Submit</span>
                      {(isCreating || isUpdating) && (
                        <span className="indicator-progress">
                          Please wait...
                          <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                        </span>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDoctorForm;

